import { Link, Typography } from '@material-ui/core';
import type { CockpitArtifactView } from '../model/cockpitViewModel';
import { formatCockpitDateTime } from '../model/formatters';
import {
  artifactAccessLabel,
  artifactRoleLabel,
  confidenceLabel,
  coverageLabel,
  lifecycleLabel,
  limitationLabel,
  provenanceLabel,
  humanizeCode,
} from '../model/semanticLabels';
import { useCockpitStyles } from './cockpitStyles';

/** Complete observable record shown only while the artifact card is open. */
const RECORD_FIELDS: {
  label: string;
  value: (artifact: CockpitArtifactView) => string;
}[] = [
  { label: 'Papel', value: a => artifactRoleLabel(a.role) },
  {
    label: 'Confiança',
    value: a => confidenceLabel(a.confidence),
  },
  {
    label: 'Ciclo de vida',
    value: a => lifecycleLabel(a.lifecycle),
  },
  { label: 'Cobertura', value: a => coverageLabel(a.coverage) },
  { label: 'Acesso', value: a => artifactAccessLabel(a.access) },
  { label: 'Origem', value: a => provenanceLabel(a.provenance) },
  {
    label: 'Tipo',
    value: a =>
      a.artifactType ? humanizeCode(a.artifactType) : 'Não informado',
  },
  {
    label: 'Etapa',
    value: a => (a.phase ? humanizeCode(a.phase) : 'Não informada'),
  },
  {
    label: 'Skill produtora',
    value: a => a.skillName ?? 'Não vinculada',
  },
  {
    label: 'Referência',
    value: a => a.path ?? 'Não informada',
  },
  { label: 'Versão', value: a => a.version ?? 'Não informada' },
  { label: 'Série do documento', value: a => a.revisionKey },
  {
    label: 'Evidências vinculadas',
    value: a => String(a.evidenceIds.length),
  },
];

function safeHttpUrl(value: string): string | null {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:'
      ? url.toString()
      : null;
  } catch {
    return null;
  }
}

export const ArtifactReference = ({
  artifact,
}: {
  artifact: CockpitArtifactView;
}) => {
  const reference = artifact.path;
  if (!reference) return <>Não informada</>;
  const href = safeHttpUrl(reference);
  return href ? (
    <Link href={href} target="_blank" rel="noopener noreferrer">
      {reference}
    </Link>
  ) : (
    <>{reference}</>
  );
};

/** The complete artifact record, read next to its authorized content. */
export const ArtifactRecordStrip = ({
  artifact,
}: {
  artifact: CockpitArtifactView;
}) => {
  const classes = useCockpitStyles();
  return (
    <dl className={classes.artifactRecordStrip} aria-label="Ficha do artefato">
      {RECORD_FIELDS.map(field => (
        <div key={field.label}>
          <dt>{field.label}</dt>
          <dd>
            {field.label === 'Referência' ? (
              <ArtifactReference artifact={artifact} />
            ) : (
              field.value(artifact)
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
};

export const ArtifactLimitations = ({
  artifact,
}: {
  artifact: CockpitArtifactView;
}) =>
  artifact.limitations.length > 0 ? (
    <Typography component="p" variant="caption">
      Limitações: {artifact.limitations.map(limitationLabel).join('; ')}
    </Typography>
  ) : null;

export function artifactIdentityLine(
  artifact: CockpitArtifactView,
  position: string,
): string {
  const version = artifact.version ?? 'versão não informada';
  const evidence = `${artifact.evidenceIds.length} evidência${
    artifact.evidenceIds.length === 1 ? '' : 's'
  } vinculada${artifact.evidenceIds.length === 1 ? '' : 's'}`;
  return `${position} · ${version} · ${evidence} · ${artifact.revisionKey}`;
}

export function artifactDateLabel(createdAt: string | null): string {
  return createdAt
    ? `Gerado em ${formatCockpitDateTime(createdAt)}`
    : 'Data não informada · ordem temporal não comprovada';
}
